import React, {useState, useEffect} from "react";
import BackButton from "components/Button/BackButton";
import SaveButton from "components/Button/SaveButton";
import { useNavigate } from "react-router-dom";
import { FormTypeAdd, FormTypeEdit } from "utils/Constant";
import { money } from "utils/helpers";
import { useModal } from "context/ModalContext";
import Select from 'react-select'
import moment from "moment";
import DatePicker from "components/Input/DatePicker";
import { FormCard, MainBodyCard, PagesFormTitle } from "components/Cards";
import { ProductService } from "services/ProductService/ProductService";
import { RentalStockService } from "services/RentalStockService/RentalRockService";

export default function RentalStockForm(){
    let navigate = useNavigate()

    const { showFail, showLoading, hideLoading, showSuccess, confirm } = useModal()
    const { fetchCreateRentalStock, fetchUpdateRentalStock, fetchRentalStockById, fetchRentalStockPrice } = RentalStockService()
    const { fetchProductList } = ProductService()
    
    let queryString = new URLSearchParams(window.location.search)
    let formType = queryString.get("formType")
    let rentalStockId = queryString.get("rentalStockId")

    const [productOptions, setProductOptions] = useState([])
    const [product, setProduct] = useState({})
    const [stockIn, setStockIn] = useState(new Date())
    const [purchasePrice, setPurchasePrice] = useState('')
    const [rentalPriceHour, setRentalPriceHour] = useState('0')
    const [rentalPriceDay, setRentalPriceDay] = useState('0')
    const [rentalPriceSession, setRentalPriceSession] = useState('0')
    const [sellingPrice, setSellingPrice] = useState('0')
    const [status, setStatus] = useState('')
    const [createBy, setCreateBy] = useState('')
    const [createDate, setCreateDate] = useState('')
    const [updateBy, setUpdateBy] = useState('')
    const [updateDate, setUpdateDate] = useState('')
    
    // Button Event
    const saveBtnClick = async () => {
        const onConfirm = await confirm()
        if (!onConfirm) return

        showLoading()

        try {
            if (formType === FormTypeAdd) {
                const res = await fetchCreateRentalStock(
                    product.value,
                    moment(stockIn).format('YYYY-MM-DDTHH:mm'),
                    purchasePrice.replaceAll(',',''),
                    rentalPriceHour.replaceAll(',',''),
                    rentalPriceDay.replaceAll(',',''),
                    rentalPriceSession.replaceAll(',',''),
                    sellingPrice.replaceAll(',', '')
                )
                if (!res) return                
            } else {
                const res = await fetchUpdateRentalStock(
                    rentalStockId,
                    product.value,
                    moment(stockIn).format('YYYY-MM-DDTHH:mm'),
                    purchasePrice.replaceAll(',',''),
                    rentalPriceHour.replaceAll(',',''),
                    rentalPriceDay.replaceAll(',',''),
                    rentalPriceSession.replaceAll(',',''),
                    sellingPrice.replaceAll(',', '')
                )
                if (!res) return
            }
        }
        finally {
            showSuccess({
                onSuccess: () => {
                    navigate("/rental-stock") 
                }
            })
            hideLoading()
        }
    }
    const backBtnClick = () => {
        navigate(-1)
    }
    
    // API Calls
    const getRentalStockPrice = async (productId) => {
        const res = await fetchRentalStockPrice(productId)
        if (!res) return

        if (res.data && Object.keys(res.data).length > 0) {
            let data = res.data

            setRentalPriceHour(money(data.rentalPriceHour))
            setRentalPriceSession(money(data.rentalPriceSession))
            setRentalPriceDay(money(data.rentalPriceDay))
        } else {
            setRentalPriceHour('0')
            setRentalPriceSession('0')
            setRentalPriceDay('0')
        }        
    }

    // Function
    const validation = () => {
        if (product === null){
            showFail('Product is required!')
            return false
        } else if (product.value === '') {
            showFail('Product is required!')
            return false
        }
        if (stockIn === null) {
            showFail('Stock In Date is required!')
            return false
        }
        if (purchasePrice === '') {
            showFail('Purchase Price is required!')
            return false
        }
        if (rentalPriceHour === '') {
            showFail('Rental Price / Hour is required!')
            return false
        }
        if (rentalPriceDay === '') {
            showFail('Rental Price / Day is required!')
            return false
        }
        if (rentalPriceSession === '') {
            showFail('Rental Price / Session is required!')
            return false
        }
        if (sellingPrice === '') {
            showFail('Selling Price is required!')
            return false
        }

        return true
    }
    
    useEffect(() => {
        const onLoad = async () => {
            showLoading()

            try {
                const res = await fetchProductList('', '', '1', 1000, 0)
                if (!res) return

                let data = res?.data?.listData
                let arr = [{ label: 'Select Product', value: '' }]

                data.map((data) => arr.push({ label: data.brandName + ' ' + data.productName.toUpperCase(), value: String(data.productId), price: String(data.price) }))

                setProductOptions(arr)

                if (formType === FormTypeEdit) {
                    const res = await fetchRentalStockById(rentalStockId)
                    if (!res) return 

                    let data = res.data

                    // Data
                    setProduct({ label: data.brandName + ' ' + data.productName.toUpperCase(), value: String(data.productId) })
                    setStockIn(data.stockIn)
                    setPurchasePrice(money(data.purchasePrice))
                    setRentalPriceHour(money(data.rentalPriceHour))
                    setRentalPriceDay(money(data.rentalPriceDay))
                    setRentalPriceSession(money(data.rentalPriceSession))
                    setSellingPrice(money(data.sellingPrice))

                    // Header
                    setStatus(data.status)
                    setCreateBy(data.createBy)
                    setCreateDate(data.createDate)
                    setUpdateBy(data.updateBy)
                    setUpdateDate(data.updateDate)
                }
            }
            finally {
                hideLoading()
            }
        }

        onLoad()
    }, [])

    return(
        <MainBodyCard>
            <div>
                <PagesFormTitle
                    title={'Rental Stock Detail'}
                    formType={formType}
                    status={status}
                    createDate={createDate}
                    updateDate={updateDate}
                    updateBy={updateBy}
                />

                <div className="mt-5 flex flex-col gap-3 ">
                    {/* Product */}
                    <FormCard title='Product'>
                        <Select 
                            options={productOptions} 
                            value={product} 
                            onChange={(e) => { 
                                setProduct(e); 
                                setSellingPrice(money(e.price)); 
                                getRentalStockPrice(e.value)
                            }} 
                            placeholder='Select Product' />
                    </FormCard>

                    {/* Stock In Date */}
                    <FormCard title='Stock In Date'>
                        <DatePicker value={stockIn} onChange={(e) => { setStockIn(e) }} className='h-11 w-full bg-white' />
                    </FormCard>
                    
                    {/* Purchase Price */}
                    <FormCard title='Purchase Price'>
                        <input className="input" value={purchasePrice} onChange={(e) => { setPurchasePrice(money(e.target.value)) }} maxLength={12} />
                    </FormCard>

                    {/* Rental Price Hour */}
                    <FormCard title='Rental Price / Hour'>
                        <input className="input" value={rentalPriceHour} onChange={(e) => { setRentalPriceHour(money(e.target.value)) }} maxLength={12} />
                    </FormCard>

                    {/* Rental Price Session */}
                    <FormCard title='Rental Price / Session'>
                        <input className="input" value={rentalPriceSession} onChange={(e) => { setRentalPriceSession(money(e.target.value)) }} maxLength={12} />
                    </FormCard>

                    {/* Rental Price Day */}
                    <FormCard title='Rental Price / Day'>
                        <input className="input" value={rentalPriceDay} onChange={(e) => { setRentalPriceDay(money(e.target.value)) }} maxLength={12} />
                    </FormCard>

                    {/* Selling Price */}
                    <FormCard title='Selling Price'>
                        <input className="input" value={sellingPrice} onChange={(e) => { setSellingPrice(money(e.target.value)) }} maxLength={12} />
                    </FormCard>
                </div>
            </div>            

            {/* Button */}
            <div className="mt-5 flex flex-wrap justify-end gap-2">
                <SaveButton onClick={saveBtnClick} visible={true} />
                <BackButton onClick={backBtnClick} />
            </div>
        </MainBodyCard>        
    )
}